import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Brain, Plus, Edit3, Trash2, Mic, Volume2, Play, Loader2,
  BookOpen, MessageSquareQuote, FileText, Zap, Star, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const TYPE_CONFIG = {
  qa: { label: 'Q&A', icon: MessageSquareQuote, color: 'text-blue-500' },
  script: { label: 'Script', icon: FileText, color: 'text-purple-500' },
  fact: { label: 'Fact', icon: Zap, color: 'text-amber-500' },
};

const SiaTraining = () => {
  const [knowledge, setKnowledge] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewText, setPreviewText] = useState('Hi, I am Sia from Visual Institute of Arts. How are you today?');
  const [previewVoiceId, setPreviewVoiceId] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const [form, setForm] = useState({
    type: 'qa', question: '', answer: '', scenario: '', script: '', content: '',
    category: 'general', priority: 5, active: true,
  });

  const fetchAll = useCallback(async () => {
    try {
      const [kRes, vRes] = await Promise.all([
        axios.get(`${API}/sia/knowledge`, { headers: authHdr() }),
        axios.get(`${API}/sia/voices`, { headers: authHdr() }),
      ]);
      setKnowledge(kRes.data || []);
      setVoices(vRes.data?.presets || []);
      if (vRes.data?.current_voice_id) setPreviewVoiceId(vRes.data.current_voice_id);
    } catch (err) { toast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = (type = 'qa') => {
    setEditing(null);
    setForm({ type, question: '', answer: '', scenario: '', script: '', content: '',
              category: 'general', priority: 5, active: true });
    setShowEditor(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      type: item.type, question: item.question || '', answer: item.answer || '',
      scenario: item.scenario || '', script: item.script || '', content: item.content || '',
      category: item.category || 'general', priority: item.priority || 5,
      active: item.active !== false,
    });
    setShowEditor(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/sia/knowledge/${editing.knowledge_id}`, form, { headers: authHdr() });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/sia/knowledge`, form, { headers: authHdr() });
        toast.success('Added to Sia\'s knowledge');
      }
      setShowEditor(false);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this knowledge item?')) return;
    try {
      await axios.delete(`${API}/sia/knowledge/${id}`, { headers: authHdr() });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/sia/knowledge/seed-defaults`, {}, { headers: authHdr() });
      toast.success(res.data.message);
      fetchAll();
    } catch { toast.error('Seed failed'); }
  };

  const previewVoice = async () => {
    if (!previewText.trim() || !previewVoiceId) return;
    setPreviewLoading(true); setAudioUrl(null);
    try {
      const res = await axios.post(`${API}/sia/voices/preview`,
        { text: previewText, voice_id: previewVoiceId },
        { headers: authHdr(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'audio/mpeg' });
      setAudioUrl(URL.createObjectURL(blob));
    } catch (err) { toast.error('Preview failed'); }
    setPreviewLoading(false);
  };

  const filtered = filter === 'all' ? knowledge : knowledge.filter(k => k.type === filter);
  const counts = {
    qa: knowledge.filter(k => k.type === 'qa').length,
    script: knowledge.filter(k => k.type === 'script').length,
    fact: knowledge.filter(k => k.type === 'fact').length,
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="sia-training">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-500" /> Sia Training Engine
          </h2>
          <p className="text-sm text-muted-foreground">
            Teach Sia about your courses, pricing, FAQs and sales scripts. She uses this on every call + WhatsApp reply.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {knowledge.length === 0 && (
            <Button onClick={seedDefaults} variant="outline">
              <Sparkles className="w-4 h-4 mr-2" /> Seed 12 Defaults
            </Button>
          )}
          <Button onClick={() => openNew('qa')} className="bg-gradient-to-r from-purple-500 to-pink-500" data-testid="add-knowledge-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Knowledge
          </Button>
        </div>
      </div>

      <Tabs defaultValue="knowledge" className="w-full">
        <TabsList>
          <TabsTrigger value="knowledge" data-testid="tab-knowledge">
            <BookOpen className="w-4 h-4 mr-2" /> Knowledge ({knowledge.length})
          </TabsTrigger>
          <TabsTrigger value="voice" data-testid="tab-voice">
            <Mic className="w-4 h-4 mr-2" /> Voice
          </TabsTrigger>
        </TabsList>

        {/* KNOWLEDGE TAB */}
        <TabsContent value="knowledge" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
              const Ic = cfg.icon;
              return (
                <Card key={key}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">{cfg.label}s</p>
                      <p className="text-2xl font-bold">{counts[key]}</p>
                    </div>
                    <Ic className={`w-6 h-6 ${cfg.color}`} />
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Filter */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'qa', 'script', 'fact'].map(t => (
              <Button key={t} size="sm" variant={filter === t ? 'default' : 'outline'}
                onClick={() => setFilter(t)}>
                {t === 'all' ? 'All' : TYPE_CONFIG[t].label}
              </Button>
            ))}
          </div>

          {/* List */}
          <div className="space-y-2">
            {filtered.length === 0 && (
              <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
                No knowledge yet. Click <strong>Seed 12 Defaults</strong> or <strong>Add Knowledge</strong>.
              </CardContent></Card>
            )}
            {filtered.map(k => {
              const cfg = TYPE_CONFIG[k.type];
              const Ic = cfg.icon;
              return (
                <Card key={k.knowledge_id} className="hover:border-primary transition"
                  data-testid={`k-row-${k.knowledge_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex gap-3 flex-1 min-w-0">
                        <Ic className={`w-5 h-5 shrink-0 mt-1 ${cfg.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex gap-2 flex-wrap mb-1">
                            <Badge variant="outline" className="text-xs">{cfg.label}</Badge>
                            <Badge variant="secondary" className="text-xs">{k.category}</Badge>
                            {k.priority >= 8 && <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300"><Star className="w-3 h-3 mr-1" />Priority</Badge>}
                            {!k.active && <Badge variant="outline" className="text-xs text-muted-foreground">Inactive</Badge>}
                          </div>
                          {k.type === 'qa' && (
                            <>
                              <div className="font-medium text-sm">{k.question}</div>
                              <div className="text-sm text-muted-foreground mt-1">{k.answer}</div>
                            </>
                          )}
                          {k.type === 'script' && (
                            <>
                              <div className="font-medium text-sm">When: {k.scenario}</div>
                              <div className="text-sm text-muted-foreground mt-1 italic">"{k.script}"</div>
                            </>
                          )}
                          {k.type === 'fact' && <div className="text-sm">{k.content}</div>}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(k)}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(k.knowledge_id)}>
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* VOICE TAB */}
        <TabsContent value="voice" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Volume2 className="w-5 h-5" /> Voice Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Select Voice</Label>
                <Select value={previewVoiceId} onValueChange={setPreviewVoiceId}>
                  <SelectTrigger data-testid="voice-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {voices.map(v => (
                      <SelectItem key={v.voice_id} value={v.voice_id}>
                        {v.name} — {v.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Text to Speak</Label>
                <Textarea rows={3} value={previewText} onChange={e => setPreviewText(e.target.value)}
                  data-testid="preview-text" />
              </div>
              <Button onClick={previewVoice} disabled={previewLoading} data-testid="preview-btn"
                className="bg-gradient-to-r from-purple-500 to-pink-500">
                {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Play className="w-4 h-4 mr-2" />}
                Generate & Listen
              </Button>
              {audioUrl && (
                <audio controls src={audioUrl} className="w-full" autoPlay data-testid="preview-audio" />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Available Voices</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {voices.map(v => (
                  <div key={v.voice_id} className={`p-3 border rounded-lg ${previewVoiceId === v.voice_id ? 'border-purple-500 bg-purple-500/5' : ''}`}>
                    <div className="font-medium">{v.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{v.description}</div>
                    <Badge variant="outline" className="mt-2 text-xs">{v.accent}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} Knowledge</DialogTitle>
            <DialogDescription>Sia uses this on every call and WhatsApp conversation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="qa">Q&A — Answer a common question</SelectItem>
                  <SelectItem value="script">Script — Handle a specific scenario</SelectItem>
                  <SelectItem value="fact">Fact — Course/policy/offer info</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.type === 'qa' && (
              <>
                <div>
                  <Label>Question</Label>
                  <Input value={form.question} onChange={e => setForm({ ...form, question: e.target.value })}
                    placeholder="e.g. What are the fees?" data-testid="form-question" />
                </div>
                <div>
                  <Label>Answer (what Sia should say)</Label>
                  <Textarea rows={3} value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })}
                    placeholder="e.g. The B.Des course is 2,50,000 total with EMI from..." data-testid="form-answer" />
                </div>
              </>
            )}
            {form.type === 'script' && (
              <>
                <div>
                  <Label>Scenario (when to use)</Label>
                  <Input value={form.scenario} onChange={e => setForm({ ...form, scenario: e.target.value })}
                    placeholder="e.g. Student hesitates on price" />
                </div>
                <div>
                  <Label>Script (Sia's response)</Label>
                  <Textarea rows={4} value={form.script} onChange={e => setForm({ ...form, script: e.target.value })}
                    placeholder="e.g. I understand the cost is a consideration..." />
                </div>
              </>
            )}
            {form.type === 'fact' && (
              <div>
                <Label>Fact</Label>
                <Textarea rows={3} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
                  placeholder="e.g. Current offer: 25% off on combo courses till March 31st" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  placeholder="fees, placement, offers..." />
              </div>
              <div>
                <Label>Priority (0-10)</Label>
                <Input type="number" min={0} max={10} value={form.priority}
                  onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} />
              <span className="text-sm">Active</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={save} data-testid="form-save">
              {editing ? 'Update' : 'Add to Sia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiaTraining;
